import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  OpenInNew as OpenInNewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import PrintStatusChip from './PrintStatusChip';
import { groupPrintBatches } from '../../utils/printerState.mjs';

const TYPE_LABELS = {
  wb_order_sticker: 'Стикер задания',
  wb_supply_qr: 'QR поставки',
  product_technical_label: 'Техническая этикетка товара',
  fulfillment_mass_batch: 'Массовая партия Fulfillment',
  ms_product_barcode: 'ШК товара МС',
};

const PHASE_LABELS = {
  queued_to_spooler: 'Передано в очередь принтера',
  printing: 'Печатается',
  paused: 'Печать приостановлена',
  offline: 'Принтер недоступен',
  error: 'Ошибка принтера',
  canceled: 'Печать отменена в Windows',
  completed: 'Печать завершена',
};

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '—'
    : date.toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' });
}

export default function PrintJobsPanel({
  jobs,
  loading,
  onRefresh,
  onReprint,
  onCancel,
  onOpenMassPdf,
  reprintingId,
  cancelingId,
  canReprint,
  title = 'Последние задания печати',
}) {
  const batches = groupPrintBatches(jobs).slice(0, 5);

  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ p: 2 }}>
        <Typography variant="titleMedium">{title}</Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={onRefresh} disabled={loading}>
          Обновить
        </Button>
      </Stack>
      {batches.length > 0 && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ px: 2, pb: 2 }}>
          {batches.map((batch) => {
            const failed = (batch.counts.failed || 0) + (batch.counts.unknown || 0);
            const inProgress = batch.total - batch.terminal;
            return (
              <Paper key={batch.batchId} variant="outlined" sx={{ px: 1.5, py: 1 }}>
                <Typography variant="labelMedium">Batch {batch.batchId.slice(0, 8)}</Typography>
                <Typography variant="caption" color={failed ? 'error.main' : 'text.secondary'} component="p">
                  {batch.total} заданий · {batch.terminal} завершено
                  {inProgress ? ` · ${inProgress} в работе` : ''}
                  {failed ? ` · ${failed} требуют внимания` : ''}
                </Typography>
              </Paper>
            );
          })}
        </Stack>
      )}
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Документ</TableCell>
              <TableCell>Принтер</TableCell>
              <TableCell>Статус</TableCell>
              <TableCell>Создано</TableCell>
              <TableCell align="right">Действия</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {jobs.map((job) => (
              <TableRow key={job.id} hover>
                <TableCell>
                  <Typography variant="bodyMedium">{TYPE_LABELS[job.document_type] || job.document_type}</Typography>
                  <Typography variant="caption" color="text.secondary" component="p">
                    {job.source_id} · batch {job.batch_id}
                  </Typography>
                </TableCell>
                <TableCell>{job.printer_uid}</TableCell>
                <TableCell>
                  <PrintStatusChip status={job.status} errorMessage={job.error_message} />
                  {job.pages_total > 0 && (
                    <Box sx={{ minWidth: 160, mt: 0.75 }}>
                      <Typography variant="caption" color="text.secondary" component="p">
                        {job.progress_phase === 'printing' || job.progress_phase === 'completed'
                          ? `Напечатано ${job.pages_printed || 0} из ${job.pages_total}`
                          : (PHASE_LABELS[job.progress_phase] || `Страниц: ${job.pages_total}`)}
                      </Typography>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(100, Math.max(0, ((job.pages_printed || 0) / job.pages_total) * 100))}
                        sx={{ mt: 0.5 }}
                      />
                    </Box>
                  )}
                  {job.progress_detail && (
                    <Typography
                      variant="caption"
                      color={job.progress_diagnostic === 'spool_job_not_yet_observed'
                        ? 'warning.main'
                        : 'text.secondary'}
                      component="p"
                      sx={{ mt: 0.5, maxWidth: 360 }}
                    >
                      {job.progress_detail}
                    </Typography>
                  )}
                  {job.reprint_of_id && (
                    <Typography variant="caption" color="text.secondary" component="p">
                      Повтор задания №{job.reprint_of_id}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatDate(job.created_at)}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    {job.document_type === 'fulfillment_mass_batch' && job.can_download && (
                      <Button
                        size="small"
                        startIcon={<OpenInNewIcon />}
                        onClick={() => onOpenMassPdf(job)}
                      >
                        Открыть PDF
                      </Button>
                    )}
                    {onCancel && job.status === 'queued' && (
                      <Button
                        size="small"
                        color="warning"
                        startIcon={String(cancelingId) === String(job.id)
                          ? <CircularProgress size={16} />
                          : <CloseIcon />}
                        disabled={Boolean(cancelingId)}
                        onClick={() => onCancel(job)}
                      >
                        Отменить
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={String(reprintingId) === String(job.id) ? <CircularProgress size={16} /> : <PrintIcon />}
                      disabled={!canReprint
                        || Boolean(reprintingId)
                        || !job.content_available
                        || ['queued', 'leased', 'spooled'].includes(job.status)}
                      onClick={() => onReprint(job)}
                    >
                      Перепечатать
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!jobs.length && !loading && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Заданий печати пока нет</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}><CircularProgress size={26} /></Box>}
    </Paper>
  );
}
